
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './Button';
import { Menu, X, ChevronDown, Calculator, ArrowRight, MessageSquare, Cpu, Code2, Globe } from 'lucide-react';
import { Page } from '../types';

interface HeaderProps {
  onNavigate?: (page: Page) => void;
}

export const Header: React.FC<HeaderProps> = ({ onNavigate }) => {
  const { t, i18n } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeMobileDropdown, setActiveMobileDropdown] = useState<string | null>(null);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'de' ? 'en' : 'de';
    i18n.changeLanguage(newLang);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const handleNavigation = (e: React.MouseEvent, page: Page) => {
    e.preventDefault();
    if (onNavigate) {
      onNavigate(page);
      setIsMobileMenuOpen(false);
      setActiveMobileDropdown(null);
    }
  };

  const scrollToSection = (e: React.MouseEvent, href: string) => {
    if (href.startsWith('#')) {
      e.preventDefault();
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        setIsMobileMenuOpen(false);
        setActiveMobileDropdown(null);
      }
    }
  };

  interface NavLink {
    name: string;
    href: string;
    dropdownItems?: string[];
    dropdownFooter?: string;
    action?: (e: React.MouseEvent) => void;
  }

  const navLinks: NavLink[] = [
    { name: t('nav.home'), href: '#' },
    {
      name: t('nav.industries'),
      href: '#services',
      dropdownItems: Object.values(t('nav.industries_items', { returnObjects: true }) as Record<string, string>),
      dropdownFooter: t('nav.industries_footer')
    },
    {
      name: t('nav.solutions'),
      href: '#solutions',
      dropdownItems: Object.values(t('nav.solutions_items', { returnObjects: true }) as Record<string, string>),
      dropdownFooter: t('nav.solutions_footer')
    },
    {
      name: t('nav.pricing'),
      href: '#pricing',
      dropdownItems: Object.values(t('nav.pricing_items', { returnObjects: true }) as Record<string, string>),
      dropdownFooter: t('nav.pricing_footer')
    },
    {
      name: t('nav.knowledge'),
      href: '#',
      dropdownItems: Object.values(t('nav.knowledge_items', { returnObjects: true }) as Record<string, string>),
      dropdownFooter: t('nav.knowledge_footer')
    },
    {
      name: t('nav.about'),
      href: '#',
      action: (e) => handleNavigation(e, 'about')
    },
    {
      name: t('nav.contact'),
      href: '#',
      action: (e) => handleNavigation(e, 'contact')
    },
  ];

  const toggleMobileDropdown = (name: string) => {
    if (activeMobileDropdown === name) {
      setActiveMobileDropdown(null);
    } else {
      setActiveMobileDropdown(name);
    }
  };

  const handleIndustryClick = (e: React.MouseEvent, item: string) => {
    e.preventDefault();

    // Mapping using translation values for both languages
    const getMappingKey = (val: string) => {
      const deItems = t('nav.industries_items', { lng: 'de', returnObjects: true }) as Record<string, string>;
      const enItems = t('nav.industries_items', { lng: 'en', returnObjects: true }) as Record<string, string>;
      const deSols = t('nav.solutions_items', { lng: 'de', returnObjects: true }) as Record<string, string>;
      const enSols = t('nav.solutions_items', { lng: 'en', returnObjects: true }) as Record<string, string>;
      const dePricing = t('nav.pricing_items', { lng: 'de', returnObjects: true }) as Record<string, string>;
      const enPricing = t('nav.pricing_items', { lng: 'en', returnObjects: true }) as Record<string, string>;
      const deKnowledge = t('nav.knowledge_items', { lng: 'de', returnObjects: true }) as Record<string, string>;
      const enKnowledge = t('nav.knowledge_items', { lng: 'en', returnObjects: true }) as Record<string, string>;

      const allDe = { ...deItems, ...deSols, ...dePricing, ...deKnowledge };
      const allEn = { ...enItems, ...enSols, ...enPricing, ...enKnowledge };

      const foundKey = Object.entries(allDe).find(([_, v]) => v === val)?.[0] ||
        Object.entries(allEn).find(([_, v]) => v === val)?.[0];
      return foundKey;
    };

    const key = getMappingKey(item);

    const mapping: Record<string, Page> = {
      "medicine": 'medicine',
      "law": 'law',
      "finance": 'finance',
      "trade": 'trade',
      "craft": 'craft',
      "estate": 'estate',
      "gastronomy": 'gastronomy',
      "custom_arch": 'custom-architecture',
      "roi": 'roi',
      "training": 'training',
      "websites": 'websites',
      "ready": 'ready-to-go',
      "articles": 'ai-articles',
      "glossary": 'ai-glossary',
      "custom": 'custom-solutions'
    };

    if (key && mapping[key]) {
      handleNavigation(e, mapping[key]);
    } else {
      setIsMobileMenuOpen(false);
    }
  };

  const headerClasses = isMobileMenuOpen
    ? 'bg-[#0B1121] border-white/10 py-4'
    : isScrolled
  const textColorClass = isMobileMenuOpen ? 'text-white' : 'text-gray-900';

  const logoSrc = isMobileMenuOpen ? "/assets/CleverDigital_dark_logo_only.png" : "/assets/CleverDigital_light_logo_only.png";

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-[60] transition-all duration-300 ${isScrolled || isMobileMenuOpen ? 'bg-white shadow-sm py-4' : 'bg-white/90 backdrop-blur-md py-6'}`}>
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center relative">

          {/* LOGO */}
          <div className="flex items-center cursor-pointer mr-10" onClick={() => { setIsMobileMenuOpen(false); if (onNavigate) onNavigate('home'); }}>
            <img src={logoSrc} alt="CleverDigital" className="h-10 w-auto object-contain" />
          </div>

          <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
            {navLinks.map((link) => (
              <div key={link.name} className={link.dropdownItems ? "group static" : "relative group"}>
                <a href={link.href} className="text-sm font-medium text-gray-600 hover:text-clever-teal transition-colors flex items-center gap-1 py-4 whitespace-nowrap"
                  onClick={(e) => {
                    if (link.action) link.action(e);
                    else if (link.name === 'Home') handleNavigation(e, 'home');
                    else if (link.href.startsWith('#')) scrollToSection(e, link.href);
                  }}>
                  {link.name}
                  {link.dropdownItems && <ChevronDown size={14} className="group-hover:rotate-180 transition-transform duration-300" />}
                </a>
                {link.dropdownItems && (
                  <div className="absolute top-full left-0 w-full bg-white border-t border-gray-100 shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform origin-top translate-y-2 group-hover:translate-y-0 z-50">
                    <div className="max-w-4xl mx-auto py-10 px-6">
                      <div className="grid grid-cols-2 gap-x-16 gap-y-6">
                        {link.dropdownItems.map((item, idx) => (
                          <a key={idx} href="#" className="group/item flex items-center" onClick={(e) => handleIndustryClick(e, item)}>
                            <span className="text-base font-medium text-gray-600 group-hover/item:text-gray-900 transition-colors relative">
                              {item}
                              <span className="absolute -bottom-1 left-0 w-0 h-px bg-clever-teal transition-all duration-300 group-hover/item:w-full"></span>
                            </span>
                          </a>
                        ))}
                      </div>
                      <div className="mt-10 pt-8 border-t border-gray-100 w-full flex items-center justify-center gap-4">
                        <span className="h-px w-12 bg-gray-200"></span>
                        <span className="text-[10px] font-mono font-bold uppercase tracking-[0.2em] text-gray-400 whitespace-nowrap">{link.dropdownFooter}</span>
                        <span className="h-px w-12 bg-gray-200"></span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
            <div className="flex items-center gap-2 ml-4">
              <Button variant="ghost" className="!py-2.5 !px-4 text-xs tracking-wider text-gray-600 hover:text-gray-900 whitespace-nowrap" onClick={(e) => handleNavigation(e, 'roi')}>
                <Calculator size={14} /> {t('nav.roi_button')}
              </Button>
              <Button variant="dark" className="!py-2.5 !px-5 text-xs tracking-wider whitespace-nowrap" onClick={(e) => scrollToSection(e, '#footer')}>{t('nav.audit_button')}</Button>

              {/* Language Switcher */}
              <button
                onClick={toggleLanguage}
                className="ml-2 flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-gray-500 hover:text-gray-900 border border-gray-200 rounded-full hover:border-gray-400 transition-all"
                title={i18n.language === 'de' ? 'Switch to English' : 'Auf Deutsch wechseln'}
              >
                <Globe size={14} />
                <span className="uppercase">{i18n.language === 'de' ? 'EN' : 'DE'}</span>
              </button>
            </div>
          </nav>

          <button className={`lg:hidden p-2 relative z-[70] focus:outline-none transition-colors duration-300 ${textColorClass}`} onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </header>

      <div className={`fixed inset-0 bg-[#0B1121] z-[55] lg:hidden transition-all duration-500 ease-[cubic-bezier(0.32,0.72,0,1)] ${isMobileMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-full pointer-events-none'}`}>
        <div className="flex flex-col h-full pt-28 px-6 pb-12 overflow-y-auto">
          <div className="flex flex-col gap-0 border-t border-white/10">
            {navLinks.map((link) => (
              <div key={link.name} className="border-b border-white/10">
                <div className="flex items-center justify-between">
                  <a href={link.href} className="text-2xl font-bold text-white block py-6 flex-1 tracking-tight active:text-clever-teal transition-colors"
                    onClick={(e) => {
                      if (link.dropdownItems) { e.preventDefault(); toggleMobileDropdown(link.name); }
                      else if (link.action) link.action(e);
                      else if (link.name === 'Home') handleNavigation(e, 'home');
                      else if (link.href.startsWith('#')) scrollToSection(e, link.href);
                      else setIsMobileMenuOpen(false);
                    }}>
                    {link.name}
                  </a>
                  {link.dropdownItems && (
                    <button onClick={() => toggleMobileDropdown(link.name)} className="p-6 text-white/50 hover:text-white transition-colors">
                      <ChevronDown size={24} className={`transition-transform duration-300 ${activeMobileDropdown === link.name ? 'rotate-180 text-white' : ''}`} />
                    </button>
                  )}
                </div>
                {link.dropdownItems && (
                  <div className={`overflow-hidden transition-all duration-500 ease-in-out ${activeMobileDropdown === link.name ? 'max-h-[800px] opacity-100 mb-6' : 'max-h-0 opacity-0'}`}>
                    <div className="flex flex-col gap-4 pl-4 border-l-2 border-clever-teal/30 ml-1">
                      {link.dropdownItems.map((item, i) => (
                        <a key={i} href="#" className="block text-gray-400 hover:text-white text-lg font-medium transition-colors flex items-center gap-2" onClick={(e) => handleIndustryClick(e, item)}>
                          <ArrowRight size={14} className="text-clever-teal" /> {item}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="mt-auto pt-12 flex flex-col gap-4">
            <Button variant="primary" className="w-full py-5 text-base justify-center uppercase tracking-widest" onClick={(e) => handleNavigation(e, 'contact')}>
              <MessageSquare size={18} /> {t('nav.contact')}
            </Button>

            {/* Mobile Language Switcher */}
            <button
              onClick={toggleLanguage}
              className="w-full flex items-center justify-center gap-2 py-4 text-base font-bold text-gray-400 hover:text-white border border-white/10 rounded-lg transition-all"
            >
              <Globe size={18} />
              <span>{i18n.language === 'de' ? 'Switch to English' : 'Auf Deutsch wechseln'}</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
